import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  getDurableStateStorage,
  PROJECT_SETUP_HISTORY_STORAGE_KEY,
  registerDurableRehydrator,
} from '../lib/durableState/durableState';
import {
  normalizeUserProjectProjectKey,
  normalizeUserProjectUserId,
} from '../lib/userProjectIdentity';

interface ProjectSetupHistoryState {
  completedByUser: Record<string, Record<string, true>>;
  legacyClaimedProjects: Record<string, true>;
  markCompleted: (
    userId: string | null | undefined,
    projectKey: string | null | undefined
  ) => void;
  clearCompleted: (
    userId: string | null | undefined,
    projectKey: string | null | undefined
  ) => void;
  claimLegacyCompletion: (
    userId: string | null | undefined,
    projectKey: string | null | undefined
  ) => boolean;
  reset: () => void;
}

const emptyHistory = { completedByUser: {}, legacyClaimedProjects: {} };
function normalizeUserId(userId: string | null | undefined): string | null {
  return normalizeUserProjectUserId(userId);
}

function normalizeProjectKey(projectKey: string | null | undefined): string | null {
  return normalizeUserProjectProjectKey(projectKey);
}

function sanitizeCompletedByUser(
  value: unknown
): Record<string, Record<string, true>> {
  if (typeof value !== 'object' || value === null) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([userId, projects]) => [normalizeUserId(userId), sanitizeProjectMap(projects)] as const)
      .filter(([userId, projects]) => userId !== null && Object.keys(projects).length > 0)
      .map(([userId, projects]) => [userId as string, projects])
  );
}

function sanitizeProjectMap(value: unknown): Record<string, true> {
  if (typeof value !== 'object' || value === null) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, completed]) => completed === true)
      .map(([projectKey]) => normalizeProjectKey(projectKey))
      .filter((projectKey): projectKey is string => projectKey !== null)
      .map((projectKey) => [projectKey, true as const])
  );
}

export const useProjectSetupHistoryStore = create<ProjectSetupHistoryState>()(
  persist(
    (set, get) => ({
      ...emptyHistory,
      markCompleted: (userId, projectKey) => {
        const normalizedUserId = normalizeUserId(userId);
        const normalizedProjectKey = normalizeProjectKey(projectKey);
        if (!normalizedUserId || !normalizedProjectKey) {
          return;
        }

        set((state) => state.completedByUser[normalizedUserId]?.[normalizedProjectKey]
          ? state
          : {
              completedByUser: {
                ...state.completedByUser,
                [normalizedUserId]: {
                  ...state.completedByUser[normalizedUserId],
                  [normalizedProjectKey]: true,
                },
              },
            });
      },
      clearCompleted: (userId, projectKey) => {
        const normalizedUserId = normalizeUserId(userId);
        const normalizedProjectKey = normalizeProjectKey(projectKey);
        if (!normalizedUserId || !normalizedProjectKey) {
          return;
        }

        set((state) => {
          const userProjects = state.completedByUser[normalizedUserId];
          if (!userProjects?.[normalizedProjectKey]) {
            return state;
          }

          const remainingUserProjects = { ...userProjects };
          delete remainingUserProjects[normalizedProjectKey];
          const completedByUser = { ...state.completedByUser };
          if (Object.keys(remainingUserProjects).length === 0) {
            delete completedByUser[normalizedUserId];
          } else {
            completedByUser[normalizedUserId] = remainingUserProjects;
          }
          return { completedByUser };
        });
      },
      claimLegacyCompletion: (userId, projectKey) => {
        const normalizedUserId = normalizeUserId(userId);
        const normalizedProjectKey = normalizeProjectKey(projectKey);
        if (
          !normalizedUserId ||
          !normalizedProjectKey ||
          get().legacyClaimedProjects[normalizedProjectKey]
        ) {
          return false;
        }

        set((state) => ({
          completedByUser: {
            ...state.completedByUser,
            [normalizedUserId]: {
              ...state.completedByUser[normalizedUserId],
              [normalizedProjectKey]: true,
            },
          },
          legacyClaimedProjects: {
            ...state.legacyClaimedProjects,
            [normalizedProjectKey]: true,
          },
        }));
        return true;
      },
      reset: () => set(emptyHistory),
    }),
    {
      name: PROJECT_SETUP_HISTORY_STORAGE_KEY,
      storage: createJSONStorage(getDurableStateStorage),
      partialize: (state) => ({
        completedByUser: state.completedByUser,
        legacyClaimedProjects: state.legacyClaimedProjects,
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        completedByUser: sanitizeCompletedByUser(
          typeof persistedState === 'object' && persistedState !== null
            ? (persistedState as { completedByUser?: unknown }).completedByUser
            : null
        ),
        legacyClaimedProjects: sanitizeProjectMap(
          typeof persistedState === 'object' && persistedState !== null
            ? (persistedState as { legacyClaimedProjects?: unknown }).legacyClaimedProjects
            : null
        ),
      }),
    }
  )
);

export function useProjectSetupCompleted(
  userId: string | null | undefined,
  projectKey: string | null | undefined
): boolean {
  const normalizedUserId = normalizeUserId(userId);
  const normalizedProjectKey = normalizeProjectKey(projectKey);
  return useProjectSetupHistoryStore((state) =>
    normalizedUserId && normalizedProjectKey
      ? state.completedByUser[normalizedUserId]?.[normalizedProjectKey] === true
      : false
  );
}

export function readProjectSetupCompleted(
  userId: string | null | undefined,
  projectKey: string | null | undefined
): boolean {
  const normalizedUserId = normalizeUserId(userId);
  const normalizedProjectKey = normalizeProjectKey(projectKey);
  return normalizedUserId && normalizedProjectKey
    ? useProjectSetupHistoryStore.getState().completedByUser[normalizedUserId]?.[normalizedProjectKey] === true
    : false;
}

export function useLegacyProjectSetupClaimed(
  projectKey: string | null | undefined
): boolean {
  const normalizedProjectKey = normalizeProjectKey(projectKey);
  return useProjectSetupHistoryStore((state) =>
    normalizedProjectKey ? state.legacyClaimedProjects[normalizedProjectKey] === true : false
  );
}

registerDurableRehydrator(() => useProjectSetupHistoryStore.persist.rehydrate());
